/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { FileBarChart2, Loader2, AlertTriangle, ChevronRight, RefreshCw, Utensils, Siren, Phone, Smile, Meh, Frown, MapPin } from "lucide-react";

const SIGN_LBL = { visage: "Visage", bras: "Bras", parole: "Parole", urgence: "Urgence" };
const ETAT_ICON = { mieux: [Smile, "Mieux", "#5CB85C"], pareil: [Meh, "Pareil", "#D4AF5A"], moins_bien: [Frown, "Moins bien", "#D9534F"] };

function UrgencesSection({ u, onPatient }) {
  if (!u) return null;
  const Suivi = ({ label, s }) => (
    <div className="rounded-2xl bg-[#F5F3EE] p-3 flex-1 min-w-[150px]">
      <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Suivi {label}</div>
      <div className="flex gap-3 mt-1 flex-wrap">
        {Object.entries(ETAT_ICON).map(([k, [I, l, c]]) => <span key={k} className="inline-flex items-center gap-1 text-[12px] text-[#0A1426]"><I size={14} style={{ color: c }} /> {s[k]} {l}</span>)}
        {s.attendus > 0 && <span className="text-[11px] text-[#8A6D2B]">· {s.attendus} en attente</span>}
      </div>
    </div>
  );
  return (
    <div data-testid="report-urgences" className="rounded-3xl border border-[#D9534F]/30 bg-[#D9534F]/5 p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2"><Siren size={18} className="text-[#D9534F]" /><h3 className="font-[Fraunces] text-lg font-bold text-[#0A1426]">Signalements urgents de la semaine</h3></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[["Signalements", u.total, "#D9534F"], ["Traités", u.traites, "#5CB85C"], ["Ouverts", u.ouverts, "#D4AF5A"], ["Délai moyen", u.delai_moyen_min == null ? "—" : u.delai_moyen_min >= 60 ? `${Math.round(u.delai_moyen_min / 60)} h` : `${u.delai_moyen_min} min`, "#0A1426"]].map(([l, v, c]) => (
          <div key={l} className="rounded-2xl bg-white p-3"><div className="font-[Fraunces] text-xl font-bold" style={{ color: c }}>{v}</div><div className="text-[11px] text-[#0A1426]/60">{l}</div></div>
        ))}
      </div>
      <div data-testid="report-urgences-signes" className="flex gap-2 flex-wrap text-[12px] text-[#0A1426]">
        <span className="font-bold">Signes VITE :</span>{Object.entries(u.signes).map(([k, v]) => <span key={k} className="px-2 py-0.5 rounded-full bg-white border border-[#E4E0D5]">{SIGN_LBL[k]} {v}</span>)}
        <span className="inline-flex items-center gap-1 text-[#0A1426]/60"><MapPin size={12} /> {u.avec_position} avec position</span>
      </div>
      <div className="flex gap-2 flex-wrap"><Suivi label="24 h" s={u.suivis.j1} /><Suivi label="Jour 3" s={u.suivis.j3} /></div>
      {u.a_rappeler.length > 0 && (
        <div data-testid="report-urgences-rappeler" className="flex flex-col gap-1.5">
          <div className="text-[11px] uppercase tracking-wider text-[#D9534F] font-bold">À rappeler — « va moins bien »</div>
          {u.a_rappeler.map((r, i) => (
            <button key={i} onClick={() => onPatient(r.patient_id)} className="text-left rounded-2xl bg-white border border-[#D9534F]/30 px-3 py-2 flex items-center gap-2">
              <Phone size={14} className="text-[#D9534F]" /><div className="flex-1"><div className="text-[13px] font-bold text-[#0A1426]">{r.nom} <span className="text-[11px] font-normal text-[#0A1426]/60">· suivi {r.etape}</span></div>{r.note && <div className="text-[12px] italic text-[#0A1426]/70">« {r.note} »</div>}</div><ChevronRight size={16} className="text-[#0A1426]/40" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const TREND = { hausse: "↗︎ en hausse", baisse: "↘︎ en baisse", stable: "→ stable" };

export default function ReportsPage() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/therapist/reports").then(({ data }) => setList(data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const open = (id) => api.get(`/therapist/reports/${id}`).then(({ data }) => setCurrent(data)).catch(() => toast.error("Rapport introuvable"));
  const generate = async () => {
    setBusy(true);
    try { const { data } = await api.post("/therapist/reports/generate"); setCurrent(data); load(); toast.success("Rapport généré"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (!list) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;

  return (
    <div className="bc-in flex flex-col gap-5" data-testid="pro-reports">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Chaque lundi 07:00</div>
          <h1 className="font-[Fraunces] text-3xl font-bold text-[#0A1426]">Rapports hebdomadaires</h1>
        </div>
        <Button data-testid="report-generate-btn" disabled={busy} onClick={generate} className="h-11 rounded-full bg-[#2BA3A0] text-white font-bold">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <><RefreshCw size={16} className="mr-2" /> Générer maintenant</>}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="flex flex-col gap-2">
          {list.length === 0 && <div className="text-sm text-[#0A1426]/60">Aucun rapport pour l'instant. Le premier arrivera lundi, ou générez-le maintenant.</div>}
          {list.map((r) => (
            <button key={r.id} data-testid={`report-row-${r.id}`} onClick={() => open(r.id)}
              className={`text-left bg-white rounded-2xl border p-4 flex items-center gap-3 hover:border-[#2BA3A0] ${current?.id === r.id ? "border-[#2BA3A0]" : "border-[#E4E0D5]"}`}>
              <FileBarChart2 size={22} className="text-[#2BA3A0]" />
              <div className="flex-1">
                <div className="font-bold text-[#0A1426]">Semaine {r.semaine}{!r.lu && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF5A] text-[#0A1426]">nouveau</span>}</div>
                <div className="text-[12px] text-[#0A1426]/60">{r.resume.a_risque} à risque · {r.resume.alertes} alertes · {r.resume.seances} séances</div>
              </div>
              <ChevronRight size={16} className="text-[#0A1426]/40" />
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {!current ? (
            <div className="bg-white rounded-3xl border border-dashed border-[#E4E0D5] p-10 text-center text-sm text-[#0A1426]/60">Sélectionnez un rapport.</div>
          ) : (
            <div data-testid="report-detail" className="bg-white rounded-3xl border border-[#E4E0D5] p-5 flex flex-col gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Semaine {current.semaine} · du {current.periode.debut} au {current.periode.fin}</div>
                <h2 className="font-[Fraunces] text-2xl font-bold text-[#0A1426]">{current.resume.a_risque} patient(s) à risque sur {current.resume.patients}</h2>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[["Alertes", current.resume.alertes, "#D9534F"], ["Séances", current.resume.seances, "#2BA3A0"], ["Patients", current.resume.patients, "#0A1426"], ["Signalements", current.resume.signalements ?? current.urgences?.total ?? 0, "#8A6D2B"]].map(([l, v, c]) => (
                  <div key={l} className="rounded-2xl p-3" style={{ backgroundColor: `${c}14` }}><div className="font-[Fraunces] text-2xl font-bold" style={{ color: c }}>{v}</div><div className="text-[11px] text-[#0A1426]/60">{l}</div></div>
                ))}
              </div>
              <UrgencesSection u={current.urgences} onPatient={(id) => navigate(`/pro/patients/${id}`)} />
              <div className="flex flex-col gap-2">
                {current.patients.map((p) => (
                  <button key={p.patient_id} data-testid={`report-patient-${p.patient_id}`} onClick={() => navigate(`/pro/patients/${p.patient_id}`)}
                    className={`text-left rounded-2xl border p-3 flex gap-3 items-start ${p.risque ? "bg-[#D9534F]/8 border-[#D9534F]/30" : "bg-[#F5F3EE] border-transparent"}`}>
                    {p.risque && <AlertTriangle size={18} className="text-[#D9534F] mt-0.5" />}
                    <div className="flex-1">
                      <div className="font-bold text-[#0A1426]">{p.nom} <span className="text-[11px] font-normal text-[#0A1426]/60">· {p.diagnostic}</span></div>
                      <div className="text-[12px] text-[#0A1426]/70">
                        {p.seances_semaine} séance(s) · tension {p.tension_moyenne || "—"} ({p.mesures_tension} mesures) · humeur {p.humeur_moyenne ?? "—"} · observance {p.observance_pct == null ? "—" : `${p.observance_pct}%`}
                      </div>
                      {p.repas && (
                        <div data-testid="report-meal-row" className="text-[11px] text-[#0A1426]/80 mt-1 flex items-center gap-1 flex-wrap">
                          <Utensils size={12} className="text-[#D4AF5A]" /> Repas : {p.repas.photos} photo(s) · équilibre {p.repas.score ?? "—"}/10{p.repas.tendance_score ? ` (${TREND[p.repas.tendance_score]})` : ""}
                          {p.repas.glycemie_moyenne ? <> · glycémie moy. {p.repas.glycemie_moyenne} mg/dL{p.repas.tendance_glycemie ? ` ${TREND[p.repas.tendance_glycemie]}` : ""}</> : null}
                        </div>
                      )}
                      {p.alertes.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{p.alertes.map((a, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white text-[#b23a36] font-bold">{a.texte}</span>)}</div>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
